import { Title } from "@/components/PredictionMarket/MarketCardList/Title";
import { DeadlineTime } from "@/components/homepage/RoundCard/components/DeadlineTime";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useHandleError } from "@/hooks/useHandleError";
import { ClockIcon, ThumbaIcon } from "@roman/shared/components/Icons";
import { useGetPredictionMarketMyEntries } from "@roman/shared/hooks/PredictionMarket/useGetPredictionMarketMyEntries";
import type { PredictionMarketMyEntry } from "@roman/shared/types/PredictionMarket";
import { cn } from "@roman/shared/utils";
import { type Choice, PredictionMarketType } from "@roman/shared/utils/predictionMarket/utils";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

type LiveOrderByType = {
  type: "entryAtDesc" | "entryCloseAtAsc";
  name: string;
};

const SORT_BY_LIST: LiveOrderByType[] = [
  {
    type: "entryAtDesc",
    name: "最新のエントリー",
  },
  {
    type: "entryCloseAtAsc",
    name: "締切期限が早い順",
  },
];

export const MyPredictionMarketEntryPage = () => {
  const [orderBy, setOrderBy] = useState(SORT_BY_LIST[0]);
  const { myEntries, isLoading } = useGetPredictionMarketMyEntries({
    swrOptions: {
      onError: useHandleError("予想市場のエントリーの取得に失敗しました"),
    },
  });

  if (!isLoading && !myEntries.length) {
    return <NoEntryData />;
  }

  const sortedEntries = myEntries.sort((a, b) => {
    if (orderBy.type === "entryAtDesc") {
      return b.entryAt - a.entryAt;
    }

    if (orderBy.type === "entryCloseAtAsc") {
      return a.entryCloseAt - b.entryCloseAt;
    }
    return 0;
  });

  return (
    <div className="flex flex-col gap-3 bg-secondary min-h-screen">
      <div className="flex gap-2 items-center w-full overflow-hidden pt-4 px-4">
        <span className="relative flex h-3 w-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#4AFFFC] opacity-75" />
          <span className="border border-none relative inline-flex rounded-full  h-3 w-3 bg-[#4AFFFC]" />
          <Line className="-bottom-1.5 left-1" />
        </span>
        <div
          className="text-xl font-extrabold z-20 text-white"
          style={{
            WebkitTextStroke: "2px hsl(var(--secondary))",
            paintOrder: "stroke",
          }}
        >
          ライブ
        </div>
      </div>

      <div className="flex flex-col gap-3 p-4">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-bold text-white">あなたのベット</h1>
          <SortBySelect selectedSortBy={orderBy} setSelectedSortBy={setOrderBy} sortByList={SORT_BY_LIST} />
        </div>

        <div className="flex flex-col gap-3">
          {isLoading && Array.from({ length: 3 }).map((_, index) => <LoadingSkeleton key={index} />)}

          {sortedEntries.map((entry) => (
            <MyEntryCard key={entry.id} predictionMarketMyEntry={entry} />
          ))}
        </div>
      </div>
    </div>
  );
};

const Line = ({ className }: { className: string }) => {
  const LinePath = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width={369} height={13} viewBox="0 0 369 13" fill="none" className="z-10">
      <path d="M0.5 5L101 12.5L163 1L212.5 8.5L304 1L370 5" stroke="#4AFFFC" />
    </svg>
  );

  return (
    <div className={cn("flex absolute", className)}>
      <LinePath />
      <LinePath />
      <LinePath />
    </div>
  );
};

export const NoEntryData = () => {
  return (
    <div className="h-screen p-4 bg-disabled">
      <div className="flex gap-2 items-center">
        <span className="border border-none relative inline-flex rounded-full h-3 w-3 bg-gray" />
        <div className="text-xl font-extrabold text-white">ライブ</div>
      </div>

      <div className="flex flex-col justify-center items-center">
        <div className="pt-8 text-white">
          <ThumbaIcon className="w-[170px] h-[140px] opacity-50" />
        </div>
        <div className="flex justify-center items-center text-sm text-center text-white font-medium py-4 px-4">
          あなたがエントリーした
          <br />
          現在開催中のクイズはありません。
        </div>
        <Link to="/prediction-market" className="flex justify-center items-center">
          <button
            type="button"
            className="py-3 px-5 border border-primary text-primary font-bold bg-white rounded-lg whitespace-nowrap"
          >
            追加のクイズに答えて賞金を獲得する
          </button>
        </Link>
      </div>
    </div>
  );
};

const MyEntryCard = ({
  predictionMarketMyEntry,
}: {
  predictionMarketMyEntry: PredictionMarketMyEntry;
}) => {
  const navigate = useNavigate();
  const myChoice = predictionMarketMyEntry.myChoice;
  const choices = predictionMarketMyEntry.choices;
  const marketType = predictionMarketMyEntry.type;

  const isPending = Date.now() > predictionMarketMyEntry.entryCloseAt;
  let color = "";
  if (marketType === PredictionMarketType.TwoChoice) {
    color = choices[0].id === myChoice.id ? "bg-chart-1/10 text-chart-1" : "bg-chart-2/10 bg-chart-2";
  }

  return (
    <div
      className=" bg-white rounded-xl overflow-hidden cursor-pointer"
      onClick={() => {
        navigate(`/prediction-market/details/${predictionMarketMyEntry.predictionMarketId}`);
      }}
    >
      <div className="p-3 px-3.5">
        <div className="flex justify-between">
          <div className="py-[3px] px-2 text-xs text-dark-gray border rounded-full border-dark-gray">
            {predictionMarketMyEntry.label}
          </div>
          {isPending ? (
            <span className="text-gray font-medium text-xs">結果待ち</span>
          ) : (
            <div className="flex gap-1 text-gray font-medium text-xs items-center">
              <ClockIcon className="h-4 w-4" />
              <DeadlineTime
                entryCloseTime={new Date(predictionMarketMyEntry.entryCloseAt).getTime()}
                className="text-dark-gray font-medium text-xs"
                showWeekday={false}
              />
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-[10px] mt-[7px]">
          {predictionMarketMyEntry.primaryImageURL && (
            <img
              src={predictionMarketMyEntry.primaryImageURL}
              alt={predictionMarketMyEntry.title}
              className="object-cover rounded-lg h-[46px] w-[46px]"
            />
          )}
          <Title
            text={predictionMarketMyEntry.title}
            className="text-black text-lg font-bold cursor-pointer text-ellipsis overflow-hidden"
          />
        </div>

        {marketType === PredictionMarketType.TwoChoice ? (
          <YesNoChoices myChoice={myChoice} choices={choices} />
        ) : (
          <MultipleChoices myChoice={myChoice} className="w-full" />
        )}

        <div className="border border-secondary/40 rounded-md overflow-hidden flex divide-x divide-secondary/20 mt-3">
          <div className="w-1/2 flex flex-col items-center">
            <div className="py-[5px] text-center border-[#91E9FF] bg-light-blue text-xs text-dark-gray w-full">
              使ったポイント
            </div>
            <div className="flex-1 flex text-black font-bold text-xl text-center items-center py-2">
              {Number(predictionMarketMyEntry.freePoints).toLocaleString()} pt
            </div>
          </div>

          <div className="w-1/2 flex flex-col items-center ">
            <div className="flex py-[5px] w-full border-[#91E9FF] bg-light-blue items-center justify-center gap-1">
              {marketType === PredictionMarketType.TwoChoice && (
                <div className={cn("rounded-xl uppercase text-xs leading-3 font-bold py-[2px] px-2", color)}>
                  {myChoice.name}
                </div>
              )}
              <div className="text-center text-xs text-dark-gray">予想獲得</div>
            </div>
            <div className="flex-1 flex text-primary font-bold text-xl text-center items-center py-2">
              {Math.round(Number(predictionMarketMyEntry.potentialFreePoints)).toLocaleString()} pt
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

type SortBySelectProps = {
  sortByList: LiveOrderByType[];
  selectedSortBy: LiveOrderByType;
  setSelectedSortBy: (sortBy: LiveOrderByType) => void;
};

export const SortBySelect = ({ selectedSortBy, setSelectedSortBy, sortByList }: SortBySelectProps) => {
  return (
    <Select
      value={selectedSortBy.type}
      onValueChange={(value) => {
        const sortBy = sortByList.find((s) => s.type === value);
        if (sortBy) setSelectedSortBy(sortBy);
      }}
    >
      <SelectTrigger
        className="max-w-fit rounded-lg text-secondary font-semibold ring-0 focus:ring-0 text-center bg-white"
        iconClassName="opacity-100 text-secondary w-5 h-5"
      >
        <p className="w-full text-center px-1 text-xs">{selectedSortBy.name}</p>
      </SelectTrigger>

      <SelectContent className="rounded-lg">
        <SelectGroup>
          {sortByList.map((s) => (
            <SelectItem key={s.type} value={s.type} className="text-xs">
              {s.name}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
};

const YesNoChoices = ({
  myChoice,
  choices,
}: {
  myChoice: PredictionMarketMyEntry["myChoice"];
  choices: Choice[];
}) => {
  const isYesChoice = choices[0].id === myChoice.id;
  const yesChoice = {
    name: choices[0].name,
    probability: isYesChoice ? myChoice.probability : 1 - myChoice.probability,
  };
  const noChoice = {
    name: choices[1].name,
    probability: isYesChoice ? 1 - myChoice.probability : myChoice.probability,
  };
  const yesPercentage =
    yesChoice.probability * 100 >= 1
      ? Math.round(yesChoice.probability * 100) === 100
        ? "99"
        : Math.round(yesChoice.probability * 100)
      : "<1";
  const noPercentage =
    noChoice.probability * 100 >= 1
      ? Math.round(noChoice.probability * 100) === 100
        ? "99"
        : Math.round(noChoice.probability * 100)
      : "<1";

  return (
    <div className="my-2">
      <div className="flex justify-between items-end w-full py-1">
        <div className="flex items-center gap-1 font-medium text-sm">
          <span className="text-xs font-bold text-[#00A199]">{yesChoice.name}</span>
          <span className="text-xs font-bold">{yesPercentage}%</span>
        </div>
        <div className="flex items-center gap-1 font-medium text-sm">
          <span className="text-xs font-bold text-[#FF5A37]">{noChoice.name}</span>
          <span className="text-xs font-bold">{noPercentage}%</span>
        </div>
      </div>

      <Progress
        value={Number((yesChoice.probability * 100).toFixed(2))}
        className="w-full h-[6px] mt-[2px]"
        indicatorColor="bg-chart-1"
        trackColor="bg-chart-2"
      />
    </div>
  );
};

export const MultipleChoices = ({
  className = "",
  myChoice,
}: {
  myChoice: PredictionMarketMyEntry["myChoice"];
  className?: string;
}) => {
  const percentage =
    myChoice.probability * 100 >= 1
      ? Math.round(myChoice.probability * 100) === 100
        ? "99"
        : Math.round(myChoice.probability * 100)
      : "<1";
  return (
    <div
      className={`relative flex items-center justify-between py-2 px-3 rounded-md text-sm bg-chart-1/10 my-4 ${className}`}
    >
      <div className="flex items-center gap-2 w-full overflow-hidden">
        <div className="flex items-center justify-center min-w-2.5 min-h-2.5 rounded-full bg-chart-1" />
        <span className="truncate font-medium text-gray-800 text-sm font-noto-sans">{myChoice.name}</span>
      </div>
      <span className="font-bold text-gray-800 ml-2 shrink-0">{percentage}%</span>
    </div>
  );
};

export const LoadingSkeleton = () => {
  return (
    <div className="bg-white rounded-xl overflow-hidden">
      <Skeleton className="h-[103px] w-full rounded-none" />

      <div className="px-[15px] pt-2.5 pb-5 flex flex-col gap-2">
        <div className="flex justify-between items-center">
          <Skeleton className="h-6 w-14" />
          <Skeleton className="h-6 w-32" />
        </div>
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    </div>
  );
};
